import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { CampaignModel, campaignApi, profileApi } from '@pukka/api';
import { createCDSCampaignEvent } from '@pukka/clevertap/customerDisplay';
import { toast } from '@pukka/toast';
import { Button, ScrollArea } from '@pukka/ui-components';
import { AxiosError } from 'axios';

import { State } from './CustomerDisplay.state';
import CustomerDisplayForm from './CustomerDisplayForm';
import { CustomerDisplayFormSchema, CustomerDisplayFormSchemaType } from './customerDisplayFormSchema';
import { constructPayload, generateErrorOutlets, today } from './utils';

interface CustomerDisplayCreateBoxProps {
  readonly onClose: () => void;
}

async function createCampaign(data: CampaignModel.CreateRequest) {
  return State.onCreateCampaign(data);
}

function CustomerDisplayCreateBox({ onClose }: CustomerDisplayCreateBoxProps) {
  const { isLoading: fetchingProfile, data: profile } = profileApi.useProfile();

  const [errorOutlets, setErrorOutlets] = useState([]);

  const methods = useForm<CustomerDisplayFormSchemaType>({
    defaultValues: {
      name: '',
      images: [],
      duration: 1,
      setTimePeriod: false,
      timePeriod: {
        startDate: today(),
        endDate: today(),
      },
      outletIds: [],
    },
    resolver: zodResolver(CustomerDisplayFormSchema),
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = handleSubmit((data) => {
    const payload = constructPayload(data);

    createCampaign(payload)
      .then(() => {
        reset();
        toast.success('Campaign was successfully created');
        onClose();
        campaignApi.invalidateCampaigns();
        createCDSCampaignEvent(payload.images.length, payload.outlet_ids, profile);
      })
      .catch((err: AxiosError<CampaignModel.CampaignErrorResponse>) => {
        if (err.response?.data?.code === '0010') {
          const outletList = generateErrorOutlets(err.message);

          setErrorOutlets(outletList);
        } else {
          toast.error(err.message);
        }
      });
  });

  return (
    <>
      <form name="campaign-create" className="relative h-full overflow-auto">
        <ScrollArea className="flex min-h-full flex-col overflow-auto bg-neutral-100 p-5 pb-32 md:pb-20">
          <div className="h-full rounded border border-neutral-200 bg-white">
            <FormProvider {...methods}>
              <CustomerDisplayForm errorOutlets={errorOutlets} />
            </FormProvider>
          </div>
        </ScrollArea>
      </form>
      <div className="absolute bottom-0 flex w-full items-center justify-end gap-x-2 bg-neutral-200 px-4 py-2.5">
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={fetchingProfile} onClick={onSubmit}>
          Save
        </Button>
      </div>
    </>
  );
}

export default CustomerDisplayCreateBox;
